import React, { useState } from 'react';
import { Table, Button, Pagination, DropdownButton, Dropdown, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BiEdit, BiCopy, BiTrash, BiSortUp, BiSortDown, BiFirstPage, BiLastPage, BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { FaCalendarAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import CopyRouteModal from './CopyRouteModal';
import DeleteRouteModal from './DeleteRouteModal';
import AvailableSchedulesModal from './AvailableSchedulesModal';
import { useNavigate, useLocation } from 'react-router-dom';
import './RouteTable.scss'; // Ensure you have the appropriate import for the styling

const RouteTable = ({
  routes,
  sortConfig,
  requestSort,
  handleEditRoute,
  handleCopyRoute,
  currentPage,
  setCurrentPage,
  totalPages,
  providers,
  fetchRoutes,
  setSuccessMessage,
}) => {
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSchedulesModal, setShowSchedulesModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [schedulesData, setSchedulesData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const providerType = searchParams.get('providerType');

  const handleOpenCopyModal = (route) => {
    setSelectedRoute(route);
    setShowCopyModal(true);
  };

  const handleCloseCopyModal = () => {
    setShowCopyModal(false);
    setSelectedRoute(null);
  };

  const handleOpenDeleteModal = (route) => {
    setSelectedRoute(route);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedRoute(null);
  };

  const handleOpenSchedulesModal = async (route) => {
    try {
      const response = await fetch(`/api/p2p_staging_schedules/${route.provider_guide_id}`);
      const data = await response.json();
      setSchedulesData(data);
      setSelectedRoute(route);
      setShowSchedulesModal(true);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const handleCloseSchedulesModal = () => {
    setShowSchedulesModal(false);
  };

  const onEditClick = (route) => {
    if(providerType === 'DCC') {
      navigate(`/routing-guide/edit/dcc/${route.provider_guide_id}?providerType=${providerType}`);
    } else {
      navigate(`/routing-guide/edit/${route.provider_guide_id}?providerType=${providerType}`);
    }
  };

  const handleFirstPage = () => setCurrentPage(1);
  const handleLastPage = () => setCurrentPage(totalPages);
  const handlePreviousPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handleJumpToPage = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <Table striped bordered hover className="mt-3">
        <thead>
          <tr>
            <th onClick={() => requestSort('origin')}>
              Origin{' '}
              {sortConfig.key === 'origin' && (
                <span>{sortConfig.direction === 'asc' ? <BiSortUp /> : <BiSortDown />}</span>
              )}
            </th>
            <th onClick={() => requestSort('destination')}>
              Destination{' '}
              {sortConfig.key === 'destination' && (
                <span>{sortConfig.direction === 'asc' ? <BiSortUp /> : <BiSortDown />}</span>
              )}
            </th>
            <th onClick={() => requestSort('provider')}>
              Provider{' '}
              {sortConfig.key === 'provider' && (
                <span>{sortConfig.direction === 'asc' ? <BiSortUp /> : <BiSortDown />}</span>
              )}
            </th>
            <th>Schedules</th> {/* Existing Schedules column */}
            <th onClick={() => requestSort('schedules_source')}>
              Schedules Source{' '}
              {sortConfig.key === 'schedules_source' && (
                <span>{sortConfig.direction === 'asc' ? <BiSortUp /> : <BiSortDown />}</span>
              )}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {routes.length > 0 ? (
            routes.map((route) => (
              <tr key={route.provider_guide_id}>
                <td>
                  <div>
                    <div>{route.origin}</div>
                    {route.origin_port_name && <div style={{ fontSize: 'small', fontWeight: 'bold', color: 'gray' }}> ({route.origin_port_name})</div>}
                  </div>
                </td>
                <td>
                  <div>
                    <div>{route.destination}</div>
                    {route.destination_port_name && <div style={{ fontSize: 'small', fontWeight: 'bold', color: 'gray' }}> ({route.destination_port_name})</div>}
                  </div>
                </td>
                <td>
                  <div>
                    <div>{route.provider_scac}</div>
                    {route.provider && <div style={{ fontSize: 'small', fontWeight: 'bold', color: 'gray' }}>{route.provider}</div>}
                  </div>
                </td>
                <td>
                  {route.has_schedules ? (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>This routing guide has at least one schedule</Tooltip>}
                    >
                      <FaCheckCircle color="green" />
                    </OverlayTrigger>
                  ) : (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>No schedules available for this routing guide</Tooltip>}
                    >
                      <FaTimesCircle color="red" />
                    </OverlayTrigger>
                  )}
                </td>
                <td>
                  {route.used_provider_scac}
                </td>
                <td>
                  <div className="actions-container">
                    <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                      <Button variant="warning" className="action-button" onClick={() => onEditClick(route)}>
                        <BiEdit />
                      </Button>
                    </OverlayTrigger>
                    {' '}
                    {providerType !=='DCC' && <OverlayTrigger placement="top" overlay={<Tooltip>Copy</Tooltip>}>
                      <Button variant="info" className="action-button" onClick={() => handleOpenCopyModal(route)}>
                        <BiCopy />
                      </Button>
                    </OverlayTrigger>}
                    {' '}
                    <OverlayTrigger placement="top" overlay={<Tooltip>Delete</Tooltip>}>
                      <Button variant="danger" className="action-button" onClick={() => handleOpenDeleteModal(route)}>
                        <BiTrash />
                      </Button>
                    </OverlayTrigger>
                    {' '}
                    <OverlayTrigger placement="top" overlay={<Tooltip>Schedules</Tooltip>}>
                      <Button variant="primary" className="action-button" onClick={() => handleOpenSchedulesModal(route)}>
                        <FaCalendarAlt />
                      </Button>
                    </OverlayTrigger>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6">No routes found.</td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination className="justify-content-center mt-3">
          <ButtonGroup>
            <OverlayTrigger placement="top" overlay={<Tooltip>First</Tooltip>}>
              <Button variant="outline-secondary" onClick={handleFirstPage} disabled={currentPage === 1}>
                <BiFirstPage />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Previous</Tooltip>}>
              <Button variant="outline-secondary" onClick={handlePreviousPage} disabled={currentPage === 1}>
                <BiChevronLeft />
              </Button>
            </OverlayTrigger>
            <DropdownButton
              as={ButtonGroup}
              variant="outline-secondary"
              title={`Jump to`}
              id="bg-nested-dropdown"
              className="jump-to-dropdown"
            >
              <div className="scrollable-dropdown">
                {[...Array(totalPages).keys()].map((page) => (
                  <Dropdown.Item key={page + 1} onClick={() => handleJumpToPage(page + 1)}>
                    Page {page + 1}
                  </Dropdown.Item>
                ))}
              </div>
            </DropdownButton>
            <OverlayTrigger placement="top" overlay={<Tooltip>Next</Tooltip>}>
              <Button variant="outline-secondary" onClick={handleNextPage} disabled={currentPage === totalPages}>
                <BiChevronRight />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Last</Tooltip>}>
              <Button variant="outline-secondary" onClick={handleLastPage} disabled={currentPage === totalPages}>
                <BiLastPage />
              </Button>
            </OverlayTrigger>
            <div className="pagination-info">
              <span>
                Page {currentPage} of {totalPages}
              </span>
            </div>
          </ButtonGroup>
        </Pagination>
      )}
      {selectedRoute && providerType != 'DCC' && (
        <CopyRouteModal
          show={showCopyModal}
          handleClose={handleCloseCopyModal}
          route={selectedRoute}
          providers={providers}
          fetchRoutes={fetchRoutes}
        />
      )}
      {selectedRoute && (
        <DeleteRouteModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          route={selectedRoute}
          fetchRoutes={fetchRoutes}
          setSuccessMessage={setSuccessMessage}
        />
      )}
      {showSchedulesModal && (
        <AvailableSchedulesModal
          show={showSchedulesModal}
          handleClose={handleCloseSchedulesModal}
          schedules={schedulesData}
          route={selectedRoute}
        />
      )}
    </>
  );
};

export default RouteTable;

